/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { Component, createRef, RefObject } from 'react';
import { ICustomTooltipProps, TOOLTIP_DURATION, TOOLTIP_POSITION, TOOLTIP_TRIGGERS, TOOLTIP_TYPE } from './types/customTooltip';
import CustomTooltipMessage from './components/CustomTooltipMessage/CustomTooltipMessage';
import { copyToBuffer } from 'src/utils/helpers';
import Portal from '~/view/components/Portal/Portal';
import './style.scss';

interface IState {
  visible: boolean;
}

export default class CustomTooltip extends Component<ICustomTooltipProps, IState> {
  timerId: NodeJS.Timeout | undefined = undefined;
  wrapperRef: RefObject<HTMLDivElement> = createRef();

  static defaultProps = {
    message: '',
    type: TOOLTIP_TYPE.INFO,
    triggerType: TOOLTIP_TRIGGERS.CLICK,
    position: TOOLTIP_POSITION.ABSOLUTE,
    duration: TOOLTIP_DURATION.EXTRA_LARGE,
    trigger: false,
    show: true,
    gapTop: 10,
  };

  state = {
    visible: Boolean(this.props.permanentVisible),
  };

  componentDidUpdate = (prevProps: ICustomTooltipProps) => {
    if (this.props.trigger && prevProps.trigger !== this.props.trigger) {
      this.setState({ visible: true }, this.hideTooltip);
    }
  };

  componentWillUnmount = () => {
    if (this.timerId) clearTimeout(this.timerId);
  };

  visibleStateHandler = (visible?: boolean) => {
    if (this.props.permanentVisible || this.props.disabled) return;

    if (this.isHoverTrigger && (visible || visible === false)) {
      this.setState({ visible });
    } else if (this.isClickTrigger) {
      if (this.props.copyData && this.isCopyType) copyToBuffer(this.props.copyData);
      this.setState({ visible: true }, this.hideTooltip);
    } else return;
  };

  hideTooltip = (hide?: boolean) => {
    if (this.timerId) clearTimeout(this.timerId);

    if (hide) {
      this.timeoutHandler();
    } else {
      this.timerId = setTimeout(() => this.timeoutHandler(), this.props.duration);
    }
  };

  timeoutHandler = () => {
    this.setState({ visible: false });
    if (this.props.trigger && this.props.disableTrigger) {
      this.props.disableTrigger();
    }
  };

  get isHoverTrigger(): boolean {
    return this.props.triggerType === TOOLTIP_TRIGGERS.HOVER;
  }

  get isClickTrigger(): boolean {
    return this.props.triggerType === TOOLTIP_TRIGGERS.CLICK;
  }

  get isCopyType(): boolean {
    return this.props.type === TOOLTIP_TYPE.COPY;
  }

  get isAvailableOverlay(): boolean {
    return this.state.visible && !this.isHoverTrigger && !this.props.permanentVisible;
  }

  get wrapperClassName(): string {
    return classNames('pos-relative', { 'ml-4': this.isCopyType, 'cursor-pointer': !this.props.disabled }, this.props.wrapperClassName);
  }

  render() {
    if (!this.props.show) return this.props.children;

    return (
      <div
        ref={this.wrapperRef}
        onPointerEnter={this.isHoverTrigger ? () => this.visibleStateHandler(true) : undefined}
        onPointerLeave={this.isHoverTrigger ? () => this.visibleStateHandler(false) : undefined}
        className={this.wrapperClassName}
      >
        <div
          onClick={this.isClickTrigger ? () => this.visibleStateHandler() : undefined}
          className={classNames('full-width full-height', this.props.contentClassName)}
        >
          {this.props.children}
        </div>

        {this.props.position === TOOLTIP_POSITION.PORTAL ? (
          <Portal>
            <CustomTooltipMessage {...this.props} visible={this.state.visible} wrapperRef={this.wrapperRef} isCopyType={this.isCopyType} />
          </Portal>
        ) : (
          <CustomTooltipMessage {...this.props} visible={this.state.visible} wrapperRef={this.wrapperRef} isCopyType={this.isCopyType} />
        )}

        {this.isAvailableOverlay ? <div onClick={() => this.hideTooltip(true)} className="tooltip__overlay" /> : null}
      </div>
    );
  }
}

export { type ICustomTooltipProps, TOOLTIP_TYPE, TOOLTIP_TRIGGERS, TOOLTIP_POSITION, TOOLTIP_DURATION };
